'use client';

import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import { trackFormSubmission } from '@/lib/analytics';

const WEB3FORMS_ACCESS_KEY = '3bff64b8-4d4c-4714-878d-e786ed51fdf4';

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  country: string;
  city: string;
  formula: string;
  delay: string;
  priority: string;
  message: string;
  consent: boolean;
}

interface ContactFormProps {
  className?: string;
}

const ContactForm = ({ className = '' }: ContactFormProps) => {
  const router = useRouter();
  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    country: '',
    city: '',
    formula: '',
    delay: '',
    priority: '',
    message: '',
    consent: false,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'error'>('idle');
  const [submitErrorMsg, setSubmitErrorMsg] = useState<string>('Veuillez réessayer ou nous contacter directement.');
  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});
  const [submittedData, setSubmittedData] = useState<FormData | null>(null);

  const formulas = [
    { value: 'Essentiel', label: 'Essentiel' },
    { value: 'Confort', label: 'Confort' },
    { value: 'Premium', label: 'Premium' },
    { value: 'Sur mesure', label: 'Sur mesure' },
  ];

  const delays = [
    { value: '0-3 mois', label: '0 à 3 mois' },
    { value: '3-6 mois', label: '3 à 6 mois' },
    { value: '6-12 mois', label: '6 à 12 mois' },
    { value: "plus d\'un an", label: "Plus d\'un an" },
  ];

  const priorities = [
    { value: 'logement', label: 'Logement' },
    { value: "création d\'entreprise", label: "Création d\'entreprise" },
    { value: 'démarches administratives', label: 'Démarches administratives' },
    { value: 'fiscalité', label: 'Fiscalité' },
    { value: 'investissement immobilier', label: 'Investissement immobilier' },
    { value: 'accompagnement complet', label: 'Accompagnement complet' },
  ];

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof FormData, string>> = {};
    if (!formData.firstName.trim()) newErrors.firstName = 'Le prénom est obligatoire';
    if (!formData.lastName.trim()) newErrors.lastName = 'Le nom est obligatoire';
    if (!formData.email.trim()) newErrors.email = "L'email est obligatoire";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Email invalide';
    if (!formData.consent) newErrors.consent = "Vous devez accepter d'être recontacté";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    if (errors[name as keyof FormData]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setIsSubmitting(true);
    setSubmitStatus('idle');
    setSubmitErrorMsg('Veuillez réessayer ou nous contacter directement.');

    const fullName = `${formData.firstName} ${formData.lastName}`.trim();

    try {
      const payload = {
        access_key: WEB3FORMS_ACCESS_KEY,
        subject: "Nouvelle demande – S\'installer au Maroc",
        from_name: fullName,
        replyto: formData.email,
        // All named fields
        name: fullName,
        email: formData.email,
        phone: formData.phone || 'Non renseigné',
        country: formData.country || 'Non renseigné',
        city: formData.city || 'Non renseigné',
        formula: formData.formula || 'Non renseigné',
        delay: formData.delay || 'Non renseigné',
        priority: formData.priority || 'Non renseigné',
        message: formData.message || 'Aucun message',
        botcheck: false,
      };

      console.log('[ContactForm] Submitting directly to Web3Forms (client-side)...');

      const res = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      });

      let data: Record<string, unknown> = {};
      try {
        data = await res.json();
      } catch (parseErr) {
        console.error('[ContactForm] Failed to parse Web3Forms response. HTTP status:', res.status, parseErr);
        setSubmitErrorMsg(`Email service returned invalid response (HTTP ${res.status})`);
        setSubmitStatus('error');
        return;
      }

      console.log('[ContactForm] Web3Forms response (HTTP', res.status, '):', JSON.stringify(data));

      if (data.success) {
        trackFormSubmission({
          form_id: 'contact_form',
          formula: formData.formula,
          delay: formData.delay,
          priority: formData.priority,
          country: formData.country,
        });
        const params = new URLSearchParams();
        params.set('name', fullName);
        if (formData.formula) params.set('formula', formData.formula);
        if (formData.delay) params.set('delay', formData.delay);
        if (formData.priority) params.set('priority', formData.priority);
        if (formData.country) params.set('country', formData.country);
        router.push(`/merci?${params.toString()}`);
      } else {
        const errMsg = (data.message as string) || 'Une erreur est survenue. Veuillez réessayer.';
        console.error('[ContactForm] Web3Forms error:', errMsg, '| HTTP status:', res.status, '| Full response:', JSON.stringify(data));
        setSubmitErrorMsg(errMsg);
        setSubmitStatus('error');
      }
    } catch (err) {
      console.error('[ContactForm] Network error submitting to Web3Forms:', err);
      setSubmitErrorMsg('Une erreur réseau est survenue. Vérifiez votre connexion et réessayez.');
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClass = (field: keyof FormData) =>
    `w-full px-4 py-3 rounded-lg border ${
      errors[field] ? 'border-red-400 bg-red-50' : 'border-border bg-white'
    } text-text-primary placeholder-text-secondary focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all duration-200 text-sm`;

  const labelClass = 'block text-sm font-semibold text-text-primary mb-1.5';

  if (submitStatus === 'error') {
    return (
      <section className={`py-16 bg-white ${className}`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-morocco border border-primary/10 p-12 text-center">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Icon name="ExclamationCircleIcon" size={40} className="text-red-600" />
            </div>
            <h3 className="text-2xl font-poppins font-bold text-text-primary mb-4">
              Erreur lors de l'envoi
            </h3>
            <p className="text-lg text-text-secondary leading-relaxed">
              {submitErrorMsg}
            </p>
            <div className="pt-3 border-t border-border">
              <button
                type="button"
                className="w-full py-4 px-8 bg-primary hover:bg-primary/90 text-white font-poppins font-bold text-base rounded-xl shadow-morocco hover:shadow-morocco-lg transform hover:scale-[1.02] transition-all duration-300"
                onClick={() => router.push('/contact')}
              >
                Réessayer
              </button>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className={`py-16 bg-white ${className}`}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-poppins font-bold text-text-primary mb-4">
            Demandez Votre Consultation Gratuite
          </h2>
          <p className="text-lg text-text-secondary max-w-2xl mx-auto">
            Remplissez ce formulaire pour recevoir une consultation personnalisée adaptée à votre projet d'installation au Maroc.
          </p>
        </div>

        <div className="bg-surface rounded-2xl shadow-morocco border border-primary/10 overflow-hidden">
          {/* Red top bar */}
          <div className="h-1.5 bg-gradient-to-r from-primary via-[#e63950] to-primary" />

          <form onSubmit={handleSubmit} className="p-8 sm:p-10 space-y-6">
            {/* Hidden Web3Forms fields */}
            <input type="hidden" name="access_key" value={WEB3FORMS_ACCESS_KEY} />
            <input type="hidden" name="subject" value="Nouvelle demande – S'installer au Maroc" />
            <input type="hidden" name="replyto" value={formData.email} />
            <input type="checkbox" name="botcheck" className="hidden" />

            {/* Row 1: Prénom + Nom */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label htmlFor="firstName" className={labelClass}>
                  Prénom <span className="text-primary">*</span>
                </label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  required
                  className={inputClass('firstName')}
                  placeholder="Votre prénom"
                />
                {errors.firstName && <p className="mt-1 text-xs text-red-500">{errors.firstName}</p>}
              </div>
              <div>
                <label htmlFor="lastName" className={labelClass}>
                  Nom <span className="text-primary">*</span>
                </label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  required
                  className={inputClass('lastName')}
                  placeholder="Votre nom"
                />
                {errors.lastName && <p className="mt-1 text-xs text-red-500">{errors.lastName}</p>}
              </div>
            </div>

            {/* Row 2: Email + Téléphone */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label htmlFor="email" className={labelClass}>
                  Email <span className="text-primary">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className={inputClass('email')}
                  placeholder="votre.email@exemple.com"
                />
                {errors.email && <p className="mt-1 text-xs text-red-500">{errors.email}</p>}
              </div>
              <div>
                <label htmlFor="phone" className={labelClass}>Téléphone / WhatsApp</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className={inputClass('phone')}
                  placeholder="+33 6 12 34 56 78"
                />
              </div>
            </div>

            {/* Row 3: Pays + Ville */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label htmlFor="country" className={labelClass}>Pays de résidence actuel</label>
                <input
                  type="text"
                  id="country"
                  name="country"
                  value={formData.country}
                  onChange={handleInputChange}
                  className={inputClass('country')}
                  placeholder="France"
                />
              </div>
              <div>
                <label htmlFor="city" className={labelClass}>Ville souhaitée au Maroc</label>
                <input
                  type="text"
                  id="city"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  className={inputClass('city')}
                  placeholder="Casablanca, Marrakech..."
                />
              </div>
            </div>

            {/* Row 4: Formule */}
            <div>
              <label htmlFor="formula" className={labelClass}>Formule choisie</label>
              <select
                id="formula"
                name="formula"
                value={formData.formula}
                onChange={handleInputChange}
                className={inputClass('formula')}
              >
                <option value="">Sélectionnez une formule</option>
                {formulas.map((f) => (
                  <option key={f.value} value={f.value}>{f.label}</option>
                ))}
              </select>
            </div>

            {/* Row 5: Délai + Priorité */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label htmlFor="delay" className={labelClass}>Délai du projet</label>
                <select
                  id="delay"
                  name="delay"
                  value={formData.delay}
                  onChange={handleInputChange}
                  className={inputClass('delay')}
                >
                  <option value="">Sélectionner...</option>
                  {delays.map((d) => (
                    <option key={d.value} value={d.value}>{d.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="priority" className={labelClass}>Priorité</label>
                <select
                  id="priority"
                  name="priority"
                  value={formData.priority}
                  onChange={handleInputChange}
                  className={inputClass('priority')}
                >
                  <option value="">Sélectionner...</option>
                  {priorities.map((p) => (
                    <option key={p.value} value={p.value}>{p.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Message */}
            <div>
              <label htmlFor="message" className={labelClass}>Message</label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                rows={4}
                className={`${inputClass('message')} resize-none`}
                placeholder="Parlez-nous de votre projet, vos objectifs, vos préoccupations..."
              />
            </div>

            {/* Consent */}
            <div className="bg-gray-50 rounded-xl p-4 border border-border">
              <label className="flex items-start gap-3 cursor-pointer group">
                <div className="relative mt-0.5 flex-shrink-0">
                  <input
                    type="checkbox"
                    name="consent"
                    checked={formData.consent}
                    onChange={handleInputChange}
                    className="sr-only"
                  />
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all duration-200 ${
                    formData.consent ? 'bg-primary border-primary' : 'border-border bg-white group-hover:border-primary/50'
                  }`}>
                    {formData.consent && <Icon name="CheckIcon" size={12} className="text-white" />}
                  </div>
                </div>
                <span className="text-sm text-text-secondary leading-relaxed">
                  J'accepte d'être recontacté concernant mon projet d'installation au Maroc et le traitement de mes données personnelles.
                </span>
              </label>
              {errors.consent && <p className="mt-2 text-xs text-red-500 ml-8">{errors.consent}</p>}
            </div>

            {/* Submit Button */}
            <div className="pt-2">
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-4 px-8 bg-primary hover:bg-primary/90 text-white font-poppins font-bold text-base rounded-xl shadow-morocco hover:shadow-morocco-lg transform hover:scale-[1.02] transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center gap-3"
              >
                {isSubmitting ? (
                  <>
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 12 0 12 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Envoi en cours...
                  </>
                ) : (
                  <>
                    <Icon name="PaperAirplaneIcon" size={20} />
                    Envoyer ma demande
                  </>
                )}
              </button>
            </div>

            <p className="text-center text-xs text-text-secondary">
              <Icon name="LockClosedIcon" size={12} className="inline mr-1" />
              Vos données sont confidentielles et ne seront jamais partagées avec des tiers.
            </p>

            {submitStatus === 'error' && (
              <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl p-4">
                <Icon name="ExclamationCircleIcon" size={20} className="text-red-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-red-700">Erreur lors de l'envoi</p>
                  <p className="text-xs text-red-600 mt-0.5">{submitErrorMsg}</p>
                </div>
              </div>
            )}
          </form>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;